import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, formatDate, formatApiError } from "../lib/format";
import { Card, Loading, KPI, EmptyState } from "../components/UI";
import { Receipt, Plus, X, TrendingDown } from "lucide-react";

const CATEGORIES = ["repairs","security","cleaning","electricity","water","legal","agent","government","miscellaneous"];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("");

  const reload = async () => {
    const r = await api.get("/expenses");
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/expenses", {
        category: f.get("category"),
        description: f.get("description"),
        amount: Number(f.get("amount")),
        expense_date: f.get("expense_date"),
        vendor: f.get("vendor"),
        receipt_url: f.get("receipt_url") || null,
      });
      toast.success("Expense recorded");
      setCreating(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const filtered = filter ? items.filter(i => i.category === filter) : items;
  const total = filtered.reduce((s, e) => s + (e.amount || 0), 0);
  const byCategory = items.reduce((acc, e) => { acc[e.category] = (acc[e.category] || 0) + (e.amount || 0); return acc; }, {});
  const topCat = Object.entries(byCategory).sort((a,b)=>b[1]-a[1])[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Expenses</h1>
          <p className="text-[#5E7166] text-sm">Property operating expenses.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold" data-testid="new-expense-btn"><Plus size={16}/> Record Expense</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPI label="Total Expenses" value={naira(items.reduce((s,e) => s + (e.amount || 0), 0))} icon={TrendingDown} accent/>
        <KPI label="Records" value={items.length} icon={Receipt}/>
        <KPI label="Top Category" value={topCat ? `${topCat[0]}` : "—"} hint={topCat ? naira(topCat[1]) : ""} icon={Receipt}/>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter("")} className={filter === "" ? "btn-emerald text-xs" : "btn-outline-emerald text-xs"}>All</button>
        {CATEGORIES.map(c => <button key={c} onClick={() => setFilter(c)} className={filter === c ? "btn-emerald text-xs" : "btn-outline-emerald text-xs"}>{c}</button>)}
      </div>

      <Card className="!p-0 overflow-hidden">
        {filtered.length === 0 ? <EmptyState icon={Receipt} title="No expenses recorded" description="Click Record Expense to add one."/> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th>Amount</th></tr></thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.id} data-testid={`expense-${e.id}`}>
                    <td>{formatDate(e.expense_date || e.created_at)}</td>
                    <td><span className="badge badge-blue">{e.category}</span></td>
                    <td>{e.description}</td>
                    <td>{e.vendor || "—"}</td>
                    <td className="font-serif font-bold text-[#C92A2A]">{naira(e.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="4" className="text-right font-bold">TOTAL (filtered)</td>
                  <td className="font-serif font-bold text-[#C92A2A]">{naira(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>}
      </Card>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="expense-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Record Expense</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Category</span>
                <select name="category" required className="input-modav mt-1">{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Description</span>
                <input name="description" required className="input-modav mt-1"/></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Amount</span>
                  <input type="number" name="amount" required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Date</span>
                  <input type="date" name="expense_date" required className="input-modav mt-1"/></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Vendor / Payee</span>
                <input name="vendor" className="input-modav mt-1"/></label>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="save-expense-btn">Save</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
